"use client";

import React, { Component, ErrorInfo, ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home, Mail } from "lucide-react";
import Link from "next/link";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to an error reporting service
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <Card className="shadow-lg">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 p-3 bg-red-100 dark:bg-red-900/40 rounded-full w-16 h-16 flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
                <CardTitle className="text-3xl font-bold text-card-foreground">
                  Unexpected Error
                </CardTitle>
                <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
                  Something went wrong in the application. Our team has been notified.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Error Details */}
                <div className="text-center">
                  <p className="text-gray-500 mb-4">
                    We apologize for the inconvenience. This error has been logged and our team will
                    investigate.
                  </p>
                  <p className="text-sm text-gray-400">
                    Error ID: {this.state.error?.name || "unknown"}
                  </p>
                </div>

                {/* Troubleshooting Steps */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-card-foreground mb-4">
                    What you can do:
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-card-50 rounded-lg">
                      <div className="w-2 h-2 bg-card-400 rounded-full"></div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">Try refreshing the page</p>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-card-50 rounded-lg">
                      <div className="w-2 h-2 bg-card-400 rounded-full"></div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">Clear your browser cache and cookies</p>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-card-50 rounded-lg">
                      <div className="w-2 h-2 bg-card-400 rounded-full"></div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">Try using a different browser</p>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-card-50 rounded-lg">
                      <div className="w-2 h-2 bg-card-400 rounded-full"></div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">Contact support if the issue persists</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                  <Button onClick={this.handleReset} className="flex-1" variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>

                  <Link href="/" className="flex-1">
                    <Button className="w-full" variant="outline">
                      <Home className="h-4 w-4 mr-2" />
                      Go Home
                    </Button>
                  </Link>
                </div>

                {/* Contact Support */}
                <div className="text-center pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-3">Need immediate assistance?</p>
                  <div className="flex justify-center gap-4">
                    <a
                      href="mailto:support@wtc.com"
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <Mail className="h-4 w-4" />
                      Email Support
                    </a>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Include Error ID: {this.state.error?.name || "unknown"} when contacting support
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook for functional components to use error boundary
export const useErrorHandler = () => {
  const handleError = (error: Error, errorInfo?: ErrorInfo) => {
    console.error("Error caught by useErrorHandler:", error, errorInfo);
    // You can add additional error reporting logic here
  };

  return { handleError };
};
